import React, { useState, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import * as d3 from 'd3';
import { 
  Brain, 
  Database, 
  BarChart3, 
  Zap, 
  Code, 
  Shield, 
  ChevronRight, 
  ChevronDown,
  BookOpen,
  Settings,
  Users,
  Lock,
  Workflow,
  FileText,
  Search,
  MessageSquare,
  Globe,
  Layers,
  Building2,
  Mail,
  Activity,
  Network,
  Server,
  Cpu,
  HardDrive,
  Monitor,
  Cloud,
  GitBranch,
  Palette,
  Layout,
  Filter,
  Eye,
  Edit,
  Trash2,
  Plus,
  Download,
  Upload,
  Share2,
  Calendar,
  Clock,
  Bell,
  CheckCircle,
  AlertTriangle,
  Info,
  Archive,
  Tag,
  Link
} from 'lucide-react';

// Lazy load documentation pages
const Overview = lazy(() => import('./documentation/Overview'));
const ConversationalAI = lazy(() => import('./documentation/ConversationalAI'));
const BlazeAIChat = lazy(() => import('./documentation/BlazeAIChat'));
const DataInsights = lazy(() => import('./documentation/DataInsights'));
const SmartAnalytics = lazy(() => import('./documentation/SmartAnalytics'));
const PredictiveInsights = lazy(() => import('./documentation/PredictiveInsights'));
const AutonomousOperations = lazy(() => import('./documentation/AutonomousOperations'));
const ObjectManagement = lazy(() => import('./documentation/ObjectManagement'));
const RecordOperations = lazy(() => import('./documentation/RecordOperations'));
const DataRelationships = lazy(() => import('./documentation/DataRelationships'));
const BulkProcessing = lazy(() => import('./documentation/BulkProcessing'));
const ProcessAutomation = lazy(() => import('./documentation/ProcessAutomation'));
const ScheduledJobs = lazy(() => import('./documentation/ScheduledJobs'));
const SecurityCollaboration = lazy(() => import('./documentation/SecurityCollaboration'));
const ReportingAnalytics = lazy(() => import('./documentation/ReportingAnalytics'));
const CustomReports = lazy(() => import('./documentation/CustomReports'));
const InteractiveDashboards = lazy(() => import('./documentation/InteractiveDashboards'));
const VisualDevelopment = lazy(() => import('./documentation/VisualDevelopment'));
const FormBuilder = lazy(() => import('./documentation/FormBuilder'));
const IntegrationAPIs = lazy(() => import('./documentation/IntegrationAPIs'));
const APIEndpoints = lazy(() => import('./documentation/APIEndpoints'));
const NotificationsCommunication = lazy(() => import('./documentation/NotificationsCommunication'));
const PushNotifications = lazy(() => import('./documentation/PushNotifications'));
const SystemAdministration = lazy(() => import('./documentation/SystemAdministration'));
const UserManagement = lazy(() => import('./documentation/UserManagement'));
const PackageDeployment = lazy(() => import('./documentation/PackageDeployment'));
const PackageManagement = lazy(() => import('./documentation/PackageManagement'));

// Default fallback component for unimplemented pages
const DefaultPage = ({ topic }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4 capitalize">
        {topic.replace(/-/g, ' ')}
      </h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        This documentation section is currently being developed. Please check back soon for detailed information.
      </p>
    </div>
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
      <div className="flex items-center">
        <Info className="h-5 w-5 text-yellow-600 mr-2" />
        <p className="text-yellow-800">
          <strong>Note:</strong> This page is under construction and will be available in the next update.
        </p>
      </div>
    </div>
  </div>
);

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-12">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// D3 Visualization Components
const PlatformArchitectureDiagram = ({ width = 600, height = 400 }) => {
  const svgRef = React.useRef();

  React.useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const architectureData = {
      nodes: [
        { id: 'ui', name: 'UI Layer', x: 300, y: 50, color: '#3B82F6' },
        { id: 'api', name: 'API Gateway', x: 300, y: 150, color: '#10B981' },
        { id: 'ai', name: 'AI Engine', x: 150, y: 250, color: '#8B5CF6' },
        { id: 'automation', name: 'Automation', x: 300, y: 250, color: '#F59E0B' },
        { id: 'data', name: 'Data Layer', x: 450, y: 250, color: '#EF4444' },
        { id: 'security', name: 'Security', x: 300, y: 350, color: '#6B7280' }
      ],
      links: [
        { source: 'ui', target: 'api' },
        { source: 'api', target: 'ai' },
        { source: 'api', target: 'automation' },
        { source: 'api', target: 'data' },
        { source: 'automation', target: 'security' },
        { source: 'data', target: 'security' }
      ]
    };

    // Draw links
    svg.selectAll('.link')
      .data(architectureData.links)
      .enter()
      .append('line')
      .attr('class', 'link')
      .attr('x1', d => architectureData.nodes.find(n => n.id === d.source).x)
      .attr('y1', d => architectureData.nodes.find(n => n.id === d.source).y)
      .attr('x2', d => architectureData.nodes.find(n => n.id === d.target).x)
      .attr('y2', d => architectureData.nodes.find(n => n.id === d.target).y)
      .attr('stroke', '#94A3B8')
      .attr('stroke-width', 2);

    // Draw nodes
    const nodeGroups = svg.selectAll('.node')
      .data(architectureData.nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', d => `translate(${d.x}, ${d.y})`);

    nodeGroups.append('circle')
      .attr('r', 40)
      .attr('fill', d => d.color)
      .attr('opacity', 0.8);

    nodeGroups.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', 'white')
      .attr('font-size', '12px')
      .attr('font-weight', 'bold')
      .text(d => d.name);

  }, [width, height]);

  return <svg ref={svgRef} width={width} height={height} />;
};

const WorkflowVisualization = ({ width = 800, height = 400 }) => {
  const svgRef = React.useRef();

  React.useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const workflowSteps = [
      { id: 1, name: 'Data Input', x: 100, y: 200, type: 'input' },
      { id: 2, name: 'AI Processing', x: 250, y: 200, type: 'process' },
      { id: 3, name: 'Validation', x: 400, y: 150, type: 'decision' },
      { id: 4, name: 'Auto Action', x: 550, y: 120, type: 'action' },
      { id: 5, name: 'Manual Review', x: 550, y: 280, type: 'manual' },
      { id: 6, name: 'Complete', x: 700, y: 200, type: 'output' }
    ];

    const connections = [
      { from: 1, to: 2 },
      { from: 2, to: 3 },
      { from: 3, to: 4, label: 'Pass' },
      { from: 3, to: 5, label: 'Review' },
      { from: 4, to: 6 },
      { from: 5, to: 6 }
    ];

    // Draw connections
    connections.forEach(conn => {
      const fromNode = workflowSteps.find(s => s.id === conn.from);
      const toNode = workflowSteps.find(s => s.id === conn.to);
      
      svg.append('line')
        .attr('x1', fromNode.x + 30)
        .attr('y1', fromNode.y)
        .attr('x2', toNode.x - 30)
        .attr('y2', toNode.y)
        .attr('stroke', '#6B7280')
        .attr('stroke-width', 2)
        .attr('marker-end', 'url(#arrowhead)');

      if (conn.label) {
        const midX = (fromNode.x + toNode.x) / 2;
        const midY = (fromNode.y + toNode.y) / 2;
        svg.append('text')
          .attr('x', midX)
          .attr('y', midY - 5)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('fill', '#6B7280')
          .text(conn.label);
      }
    });

    // Add arrow marker
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 5)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 5)
      .attr('markerHeight', 5)
      .append('path')
      .attr('d', 'M 0,-5 L 10 ,0 L 0,5')
      .attr('fill', '#6B7280');

    // Draw workflow steps
    workflowSteps.forEach(step => {
      const group = svg.append('g')
        .attr('transform', `translate(${step.x}, ${step.y})`);

      const colors = {
        input: '#10B981',
        process: '#3B82F6',
        decision: '#F59E0B',
        action: '#8B5CF6',
        manual: '#EF4444',
        output: '#6B7280'
      };

      if (step.type === 'decision') {
        group.append('polygon')
          .attr('points', '-30,0 0,-20 30,0 0,20')
          .attr('fill', colors[step.type])
          .attr('opacity', 0.8);
      } else {
        group.append('rect')
          .attr('x', -30)
          .attr('y', -15)
          .attr('width', 60)
          .attr('height', 30)
          .attr('rx', 5)
          .attr('fill', colors[step.type])
          .attr('opacity', 0.8);
      }

      group.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .attr('fill', 'white')
        .attr('font-size', '10px')
        .attr('font-weight', 'bold')
        .text(step.name);
    });

  }, [width, height]);

  return <svg ref={svgRef} width={width} height={height} />;
};

const Documentation = () => {
  const [selectedTopic, setSelectedTopic] = useState('overview');
  const [expandedSections, setExpandedSections] = useState([
    'platform-features', 
    'ai-intelligence', 
    'data-management', 
    'reporting-analytics', 
    'workflow-automation', 
    'visual-development',
    'integration-apis',
    'security-collaboration',
    'system-administration',
    'notifications-communication',
    'package-deployment'
  ]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Map topics to their corresponding components
  const getTopicComponent = (topic) => {
    switch(topic) {
      case 'overview':
        return <Overview />;
      
      // AI & Intelligence
      case 'conversational-ai':
        return <ConversationalAI />;
      case 'data-insights':
        return <DataInsights />;
      case 'smart-analytics':
        return <SmartAnalytics />;
      case 'predictive-insights':
        return <PredictiveInsights />;
      case 'blaze-ai-chat':
        return <BlazeAIChat />;
      case 'autonomous-operations':
        return <AutonomousOperations />;
      case 'ai-anomaly-detection':
        return <BlazeAIChat />;
      
      // Data Management
      case 'object-management':
        return <ObjectManagement />;
      case 'record-operations':
        return <RecordOperations />;
      case 'data-relationships':
        return <DataRelationships />;
      case 'bulk-processing':
        return <BulkProcessing />;
      case 'field-types':
      case 'data-validation':
      case 'record-history':
        return <ObjectManagement />;
      
      // Reporting & Analytics
      case 'custom-reports':
        return <CustomReports />;
      case 'interactive-dashboards':
        return <InteractiveDashboards />;
      case 'real-time-analytics':
      case 'data-export':
      case 'executive-dashboards':
      case 'chart-visualization':
        return <ReportingAnalytics />;
      
      // Workflow & Automation
      case 'process-automation':
        return <ProcessAutomation />;
      case 'scheduled-jobs':
        return <ScheduledJobs />;
      case 'custom-functions':
      case 'event-triggers':
      case 'approval-workflows':
      case 'automation-builder':
      case 'job-queue':
        return <ProcessAutomation />;
      
      // Visual Development
      case 'visual-page-builder':
        return <VisualDevelopment />;
      case 'form-builder':
        return <FormBuilder />;
      case 'layout-designer':
      case 'home-designer':
      case 'custom-login-design':
      case 'template-system':
        return <VisualDevelopment />;
      
      // Integration & APIs
      case 'api-endpoints':
        return <APIEndpoints />;
      case 'oauth-integration':
      case 'web-to-record':
      case 'third-party-apis':
      case 'webhook-management':
      case 'custom-domains':
        return <IntegrationAPIs />;
      
      // Security & Collaboration
      case 'security-collaboration':
      case 'role-based-access':
      case 'profile-management':
      case 'sharing-rules':
      case 'team-collaboration':
      case 'audit-trails':
      case 'field-level-security':
      case 'business-logic-security':
        return <SecurityCollaboration />;
      
      // System Administration
      case 'organization-management':
        return <SystemAdministration />;
      case 'user-management':
        return <UserManagement />;
      case 'group-management':
      case 'currency-settings':
      case 'master-admin':
      case 'system-logs':
        return <SystemAdministration />;
      
      // Notifications & Communication
      case 'push-notifications':
        return <PushNotifications />;
      case 'email-service':
      case 'approval-notifications':
      case 'notification-templates':
      case 'communication-log':
        return <NotificationsCommunication />;
      
      // Package & Deployment
      case 'package-management':
        return <PackageManagement />;
      case 'package-creator':
      case 'deployment-tools':
      case 'app-marketplace':
      case 'version-control':
      case 'environment-management':
        return <PackageDeployment />;
      
      default:
        return <DefaultPage topic={topic} />;
    }
  };

  const documentationStructure = [
    {
      id: 'overview',
      title: 'Platform Overview',
      icon: BookOpen,
      children: []
    },
    {
      id: 'platform-features',
      title: 'Platform Features',
      icon: Layers,
      children: [
        {
          id: 'ai-intelligence',
          title: 'AI & Intelligence',
          icon: Brain,
          children: [
            { id: 'conversational-ai', title: 'Conversational AI', icon: MessageSquare },
            { id: 'blaze-ai-chat', title: 'Blaze AI Chat Bot', icon: Brain },
            { id: 'data-insights', title: 'Data Insights', icon: BarChart3 },
            { id: 'autonomous-operations', title: 'Autonomous Operations', icon: Settings },
            { id: 'smart-analytics', title: 'Smart Analytics', icon: Search },
            { id: 'ai-anomaly-detection', title: 'AI Anomaly Detection', icon: Shield },
            { id: 'predictive-insights', title: 'Predictive Insights', icon: Brain }
          ]
        },
        {
          id: 'data-management',
          title: 'Data Management',
          icon: Database,
          children: [
            { id: 'object-management', title: 'Object Management', icon: Database },
            { id: 'record-operations', title: 'Record Operations', icon: FileText },
            { id: 'data-relationships', title: 'Data Relationships', icon: Globe },
            { id: 'bulk-processing', title: 'Bulk Processing', icon: Layers },
            { id: 'field-types', title: 'Advanced Field Types', icon: Settings },
            { id: 'data-validation', title: 'Data Validation', icon: Shield },
            { id: 'record-history', title: 'Record History & Versioning', icon: FileText }
          ]
        },
        {
          id: 'reporting-analytics',
          title: 'Reporting & Analytics',
          icon: BarChart3,
          children: [
            { id: 'custom-reports', title: 'Custom Reports', icon: FileText },
            { id: 'interactive-dashboards', title: 'Interactive Dashboards', icon: BarChart3 },
            { id: 'real-time-analytics', title: 'Real-time Analytics', icon: Zap },
            { id: 'data-export', title: 'Data Export', icon: Database },
            { id: 'executive-dashboards', title: 'Executive Dashboards', icon: BarChart3 },
            { id: 'chart-visualization', title: 'Chart & Visualization', icon: BarChart3 }
          ]
        },
        {
          id: 'workflow-automation',
          title: 'Workflow & Automation',
          icon: Workflow,
          children: [
            { id: 'process-automation', title: 'Process Automation', icon: Workflow },
            { id: 'scheduled-jobs', title: 'Scheduled Jobs', icon: Settings },
            { id: 'custom-functions', title: 'Custom Functions', icon: Code },
            { id: 'event-triggers', title: 'Event Triggers', icon: Zap },
            { id: 'approval-workflows', title: 'Approval Workflows', icon: Users },
            { id: 'automation-builder', title: 'Visual Automation Builder', icon: Workflow },
            { id: 'job-queue', title: 'Job Queue Management', icon: Layers }
          ]
        },
        {
          id: 'visual-development',
          title: 'Visual Development',
          icon: Code,
          children: [
            { id: 'visual-page-builder', title: 'Visual Page Builder', icon: Code },
            { id: 'form-builder', title: 'Form Builder', icon: FileText },
            { id: 'layout-designer', title: 'Layout Designer', icon: Layers },
            { id: 'home-designer', title: 'Home Page Designer', icon: Code },
            { id: 'custom-login-design', title: 'Custom Login Design', icon: Lock },
            { id: 'template-system', title: 'Template System', icon: FileText }
          ]
        },
        {
          id: 'integration-apis',
          title: 'Integration & APIs',
          icon: Globe,
          children: [
            { id: 'api-endpoints', title: 'API Endpoints', icon: Globe },
            { id: 'oauth-integration', title: 'OAuth2 Integration', icon: Lock },
            { id: 'web-to-record', title: 'Web-to-Record', icon: Globe },
            { id: 'third-party-apis', title: 'Third-party APIs', icon: Layers },
            { id: 'webhook-management', title: 'Webhook Management', icon: Zap },
            { id: 'custom-domains', title: 'Custom Domains', icon: Globe }
          ]
        },
        {
          id: 'security-collaboration',
          title: 'Security & Collaboration',
          icon: Shield,
          children: [
            { id: 'role-based-access', title: 'Role-based Access', icon: Lock },
            { id: 'profile-management', title: 'Profile Management', icon: Users },
            { id: 'sharing-rules', title: 'Sharing Rules', icon: Users },
            { id: 'team-collaboration', title: 'Team Collaboration', icon: Users },
            { id: 'audit-trails', title: 'Audit Trails', icon: FileText },
            { id: 'field-level-security', title: 'Field Level Security', icon: Shield },
            { id: 'business-logic-security', title: 'Business Logic Security', icon: Lock }
          ]
        },
        {
          id: 'system-administration',
          title: 'System Administration',
          icon: Settings,
          children: [
            { id: 'organization-management', title: 'Organization Management', icon: Building2 },
            { id: 'user-management', title: 'User Management', icon: Users },
            { id: 'group-management', title: 'Group Management', icon: Users },
            { id: 'currency-settings', title: 'Multi-Currency Settings', icon: Globe },
            { id: 'master-admin', title: 'Master Administration', icon: Settings },
            { id: 'system-logs', title: 'System Logs & Monitoring', icon: FileText }
          ]
        },
        {
          id: 'notifications-communication',
          title: 'Notifications & Communication',
          icon: MessageSquare,
          children: [
            { id: 'push-notifications', title: 'Push Notifications (FCM)', icon: MessageSquare },
            { id: 'email-service', title: 'Email Service', icon: Mail },
            { id: 'approval-notifications', title: 'Approval Notifications', icon: Users },
            { id: 'notification-templates', title: 'Notification Templates', icon: FileText },
            { id: 'communication-log', title: 'Communication Log', icon: MessageSquare }
          ]
        },
        {
          id: 'package-deployment',
          title: 'Package & Deployment',
          icon: Layers,
          children: [
            { id: 'package-management', title: 'Package Management', icon: Layers },
            { id: 'package-creator', title: 'Package Creator', icon: Code },
            { id: 'deployment-tools', title: 'Deployment Tools', icon: Settings },
            { id: 'app-marketplace', title: 'App Marketplace', icon: Globe },
            { id: 'version-control', title: 'Version Control', icon: FileText },
            { id: 'environment-management', title: 'Environment Management', icon: Settings }
          ]
        }
      ]
    }
  ];

  const renderTableOfContents = (items, level = 0) => {
    return items.map((item) => {
      const Icon = item.icon;
      const isExpanded = expandedSections.includes(item.id);
      const hasChildren = item.children && item.children.length > 0;
      
      return (
        <div key={item.id} className={`${level > 0 ? 'ml-4' : ''}`}>
          <div
            className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-100 ${
              selectedTopic === item.id ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500' : 'text-gray-700'
            }`}
            onClick={() => {
              if (hasChildren) {
                toggleSection(item.id);
              } else {
                setSelectedTopic(item.id);
              }
            }}
          >
            <div className="flex items-center space-x-2">
              <Icon className="h-4 w-4" />
              <span className="text-sm font-medium">{item.title}</span>
            </div>
            {hasChildren && (
              isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
            )}
          </div>
          
          {hasChildren && isExpanded && (
            <div className="mt-1">
              {renderTableOfContents(item.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Platform Documentation</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive guides and documentation for the Aquargin enterprise automation platform
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Table of Contents - 25% width */}
          <div className="w-1/4 bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-fit sticky top-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Table of Contents</h2>
            <div className="space-y-2 max-h-[70vh] overflow-y-auto">
              {renderTableOfContents(documentationStructure)}
            </div>
          </div>

          {/* Content Area - 75% width */}
          <motion.div 
            key={selectedTopic}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="w-3/4 bg-white rounded-lg shadow-sm border border-gray-200 p-8"
          >
            <Suspense fallback={<LoadingSpinner />}>
              {getTopicComponent(selectedTopic)}
            </Suspense>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Documentation; 